import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg, fmtKST, DROP_STATUSES } from "@/lib/api";
import { StatusBadge, EmptyState } from "@/components/Bits";
import SpecialDropFormDialog from "@/components/admin/SpecialDropFormDialog";
import { StatusFilter } from "@/components/admin/DropsAdmin";

const Row = ({ sd, onEdit, onDelete, onStatus }) => (
  <div data-testid={`admin-special-${sd.id}`} className="flex flex-wrap items-center gap-4 rounded-2xl border border-white/5 bg-zinc-900/60 p-4 sm:p-5">
    <div className="min-w-0 flex-1 basis-full sm:basis-0">
      <div className="flex flex-wrap items-center gap-2">
        <StatusBadge status={sd.claims_closed ? "closed" : sd.status} label={sd.claims_closed ? "Claims closed" : undefined} />
        {sd.item_types.map((t) => <span key={t} className="text-xs rounded-full bg-amber-500/15 text-amber-200 px-2 py-0.5 font-semibold">{t}</span>)}
      </div>
      <div className="font-display text-lg font-bold mt-2 truncate">{sd.name}</div>
      <div className="text-xs text-zinc-500 mt-1">
        {sd.items.length} items · {sd.total_claimed}/{sd.total_quantity} claimed · Pay to {sd.pay_to}{sd.claims_close ? ` · Closes ${fmtKST(sd.claims_close)}` : ""}
      </div>
    </div>
    <div className="flex items-center gap-1">
      <Select value={sd.status} onValueChange={(v) => onStatus(sd, v)}>
        <SelectTrigger data-testid={`admin-special-status-${sd.id}`} className="h-9 w-44 mr-1 bg-zinc-950 border-zinc-800 text-xs"><SelectValue /></SelectTrigger>
        <SelectContent>{DROP_STATUSES.map(([v, l]) => <SelectItem key={v} value={v} data-testid={`admin-special-status-option-${v}`}>{l}</SelectItem>)}</SelectContent>
      </Select>
      <Link to={`/special/${sd.id}`} data-testid={`admin-special-view-${sd.id}`} className="p-2 rounded-lg text-zinc-400 hover:text-white hover:bg-white/5 transition-colors" aria-label="View"><ExternalLink className="h-4 w-4" /></Link>
      <button type="button" data-testid={`admin-special-edit-${sd.id}`} onClick={() => onEdit(sd)} className="p-2 rounded-lg text-zinc-400 hover:text-white hover:bg-white/5 transition-colors" aria-label="Edit"><Pencil className="h-4 w-4" /></button>
      <button type="button" data-testid={`admin-special-delete-${sd.id}`} onClick={() => onDelete(sd)} className="p-2 rounded-lg text-zinc-400 hover:text-red-300 hover:bg-red-500/10 transition-colors" aria-label="Delete"><Trash2 className="h-4 w-4" /></button>
    </div>
  </div>
);

export default function SpecialDropsAdmin({ refreshKey = 0 }) {
  const [rows, setRows] = useState(null);
  const [editing, setEditing] = useState(null);
  const [picked, setPicked] = useState([]);
  const shown = rows && picked.length ? rows.filter((sd) => picked.includes(sd.status)) : rows;
  const load = useCallback(() => {
    api.get("/special-drops").then((r) => setRows(r.data)).catch(() => setRows([]));
  }, []);
  useEffect(() => { load(); }, [load, refreshKey]);

  const setStatus = async (sd, status) => {
    try {
      await api.patch(`/special-drops/${sd.id}/status`, { status });
      toast.success(`Status: ${Object.fromEntries(DROP_STATUSES)[status]}`);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const remove = async (sd) => {
    if (!window.confirm(`Delete "${sd.name}" and all its claims?`)) return;
    try {
      await api.delete(`/special-drops/${sd.id}`);
      toast.success("Special drop deleted");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div>
      <Button data-testid="admin-new-special-button" onClick={() => setEditing({})} className="rounded-xl bg-gradient-to-r from-amber-500 to-pink-600 text-white font-semibold hover:opacity-90">
        <Plus className="h-4 w-4 mr-1.5" /> New special drop
      </Button>
      {rows?.length > 0 && <div className="mt-4"><StatusFilter picked={picked} setPicked={setPicked} prefix="admin-special" /></div>}
      <div className="mt-5 space-y-3" data-testid="admin-special-list">
        {rows === null && <div className="text-zinc-500 text-sm">Loading…</div>}
        {rows?.length === 0 && <EmptyState testId="admin-special-empty" title="No special drops yet" hint="Create one for merch, snapism or washi tape." />}
        {rows?.length > 0 && shown.length === 0 && <EmptyState testId="admin-special-filter-empty" title="No special drops with these statuses" />}
        {shown?.map((sd) => <Row key={sd.id} sd={sd} onEdit={setEditing} onDelete={remove} onStatus={setStatus} />)}
      </div>
      {editing && <SpecialDropFormDialog sd={editing} onClose={() => setEditing(null)} onSaved={load} />}
    </div>
  );
}
